import { Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { MotionConfig } from "motion/react";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { ShaderBackground } from "./components/background/ShaderBackground";
import { HomePage } from "./pages/HomePage";
import { HOME_TITLE, pageTitle } from "./lib/title";
import { STATIC_PAGES, ALIAS_PAGES } from "./lib/pages";
import { usePathname } from "./lib/usePathname";

const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage").then((m) => ({ default: m.AuthCallbackPage })));
const BlogIndex = lazy(() => import("./pages/BlogIndex").then((m) => ({ default: m.BlogIndex })));
const BlogPost = lazy(() => import("./pages/BlogPost").then((m) => ({ default: m.BlogPost })));
const BuiltPage = lazy(() => import("./pages/BuiltPage").then((m) => ({ default: m.BuiltPage })));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage").then((m) => ({ default: m.FeaturesPage })));
const ComparePage = lazy(() => import("./pages/ComparePage").then((m) => ({ default: m.ComparePage })));
const BotsPage = lazy(() => import("./pages/developers/BotsPage").then((m) => ({ default: m.BotsPage })));
const AddonsPage = lazy(() => import("./pages/developers/AddonsPage").then((m) => ({ default: m.AddonsPage })));
const PluginsPage = lazy(() => import("./pages/developers/PluginsPage").then((m) => ({ default: m.PluginsPage })));
const ApisPage = lazy(() => import("./pages/developers/ApisPage").then((m) => ({ default: m.ApisPage })));
const VoicePage = lazy(() => import("./pages/developers/VoicePage").then((m) => ({ default: m.VoicePage })));
const DesignSystemPage = lazy(() => import("./pages/developers/DesignSystemPage").then((m) => ({ default: m.DesignSystemPage })));
const ContributingPage = lazy(() => import("./pages/developers/ContributingPage").then((m) => ({ default: m.ContributingPage })));
const DevelopersPage = lazy(() => import("./pages/DevelopersPage").then((m) => ({ default: m.DevelopersPage })));
const SelfHostingPage = lazy(() => import("./pages/SelfHostingPage").then((m) => ({ default: m.SelfHostingPage })));
const SupportPage = lazy(() => import("./pages/SupportPage").then((m) => ({ default: m.SupportPage })));
const InviteFriendPage = lazy(() => import("./pages/support/InviteFriendPage").then((m) => ({ default: m.InviteFriendPage })));
const NoVoicePage = lazy(() => import("./pages/support/NoVoicePage").then((m) => ({ default: m.NoVoicePage })));
const MicrophonePage = lazy(() => import("./pages/support/MicrophonePage").then((m) => ({ default: m.MicrophonePage })));
const CantConnectPage = lazy(() => import("./pages/support/CantConnectPage").then((m) => ({ default: m.CantConnectPage })));
const InstallPage = lazy(() => import("./pages/support/InstallPage").then((m) => ({ default: m.InstallPage })));
const VoiceWontStartPage = lazy(() => import("./pages/support/VoiceWontStartPage").then((m) => ({ default: m.VoiceWontStartPage })));
const SoundProblemsPage = lazy(() => import("./pages/support/SoundProblemsPage").then((m) => ({ default: m.SoundProblemsPage })));
const ScreenShareEchoPage = lazy(() => import("./pages/support/ScreenShareEchoPage").then((m) => ({ default: m.ScreenShareEchoPage })));
const ServerClockPage = lazy(() => import("./pages/support/ServerClockPage").then((m) => ({ default: m.ServerClockPage })));
const ServerIdentityPage = lazy(() => import("./pages/support/ServerIdentityPage").then((m) => ({ default: m.ServerIdentityPage })));
const ServerRestartPage = lazy(() => import("./pages/support/ServerRestartPage").then((m) => ({ default: m.ServerRestartPage })));
const ServerWontStartPage = lazy(() => import("./pages/support/ServerWontStartPage").then((m) => ({ default: m.ServerWontStartPage })));
const AppUpdatesPage = lazy(() => import("./pages/support/AppUpdatesPage").then((m) => ({ default: m.AppUpdatesPage })));
const HostServerPage = lazy(() => import("./pages/support/HostServerPage").then((m) => ({ default: m.HostServerPage })));
const JoinServerPage = lazy(() => import("./pages/support/JoinServerPage").then((m) => ({ default: m.JoinServerPage })));
const MakeInvitePage = lazy(() => import("./pages/support/MakeInvitePage").then((m) => ({ default: m.MakeInvitePage })));
const AccountOrGuestPage = lazy(() => import("./pages/support/AccountOrGuestPage").then((m) => ({ default: m.AccountOrGuestPage })));
const VoiceBasicsPage = lazy(() => import("./pages/support/VoiceBasicsPage").then((m) => ({ default: m.VoiceBasicsPage })));
const RolesPage = lazy(() => import("./pages/support/RolesPage").then((m) => ({ default: m.RolesPage })));
const ThreadsPage = lazy(() => import("./pages/support/ThreadsPage").then((m) => ({ default: m.ThreadsPage })));
const EmojisPage = lazy(() => import("./pages/support/EmojisPage").then((m) => ({ default: m.EmojisPage })));
const BackupPage = lazy(() => import("./pages/support/BackupPage").then((m) => ({ default: m.BackupPage })));
const ChangelogIndex = lazy(() => import("./pages/ChangelogIndex").then((m) => ({ default: m.ChangelogIndex })));
const ChangelogEntry = lazy(() => import("./pages/ChangelogEntry").then((m) => ({ default: m.ChangelogEntry })));
const SponsorsPage = lazy(() => import("./pages/SponsorsPage").then((m) => ({ default: m.SponsorsPage })));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy").then((m) => ({ default: m.PrivacyPolicy })));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines").then((m) => ({ default: m.CommunityGuidelines })));
const SecurityPolicy = lazy(() => import("./pages/SecurityPolicy").then((m) => ({ default: m.SecurityPolicy })));
const DownloadPage = lazy(() => import("./pages/DownloadPage").then((m) => ({ default: m.DownloadPage })));
const InvitePage = lazy(() => import("./pages/InvitePage").then((m) => ({ default: m.InvitePage })));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse").then((m) => ({ default: m.TermsOfUse })));
const WhyGryt = lazy(() => import("./pages/WhyGryt").then((m) => ({ default: m.WhyGryt })));
const NotFound = lazy(() => import("./pages/NotFound").then((m) => ({ default: m.NotFound })));

/**
 * Derived from the same list the build scripts read, so a page cannot have a prerendered
 * title and a different client-side one — or exist in one list and not another.
 */
const pageTitles: Record<string, string> = {
  '/': HOME_TITLE,
  '/auth/callback': HOME_TITLE,
  ...Object.fromEntries(
    STATIC_PAGES.map((p) => [`/${p.path}`, pageTitle(p.title)]),
  ),
  ...Object.fromEntries(
    ALIAS_PAGES.map((a) => {
      const target = STATIC_PAGES.find((p) => p.path === a.of);
      if (!target) throw new Error(`alias /${a.path} points at unknown page ${a.of}`);
      return [`/${a.path}`, pageTitle(target.title)];
    }),
  ),
};

/**
 * How long to keep looking for a `#hash` target after a route change. Pages are lazy, so
 * the element does not exist yet: measured at 573ms with a cold chunk, bounded at three.
 */
const HASH_TARGET_TIMEOUT_MS = 3000;

/**
 * How long to keep the target in place after arriving. The home page is 54,000px of images
 * still settling, so it drifts. Any scroll, wheel, touch or key cancels this.
 */
const HASH_SETTLE_MS = 1500;

function ScrollAndTitle() {
  const { pathname, hash } = useLocation();
  const page = usePathname();

  useEffect(() => {
    const title = pageTitles[page];
    if (title) document.title = title;
  }, [page]);

  /**
   * Scroll to the `#hash` when there is one, and to the top when there is not. React Router
   * does not, so a plain `<Link to="/#download">` needs no scrollIntoView at the call site.
   */
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    // A hash reaches us percent-encoded, and `getElementById` would miss any id
    // that is not plain ASCII.
    let id: string;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      id = hash.slice(1);
    }
    if (!id) {
      window.scrollTo(0, 0);
      return;
    }

    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const startedAt = performance.now();
    let raf = 0;
    let settleUntil = 0;
    let cancelled = false;

    // The reader has taken over. Stop moving the page under them.
    const surrender = () => {
      cancelled = true;
    };
    const events = ["wheel", "touchstart", "keydown"] as const;
    for (const event of events) {
      window.addEventListener(event, surrender, { passive: true, once: true });
    }

    const scrollTo = (target: Element) =>
      target.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });

    const tick = () => {
      if (cancelled) return;
      const target = document.getElementById(id);
      const now = performance.now();

      if (!target) {
        if (now - startedAt < HASH_TARGET_TIMEOUT_MS) raf = requestAnimationFrame(tick);
        return;
      }

      // Found it. Scroll, then hold it there while the images above finish
      // arriving and pushing it around.
      if (settleUntil === 0) settleUntil = now + HASH_SETTLE_MS;
      scrollTo(target);
      if (now < settleUntil) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      for (const event of events) window.removeEventListener(event, surrender);
    };
    // `pathname` too: the same `#download` reached from two different routes has to scroll
    // both times, and the hash alone does not change between them.
  }, [pathname, hash]);

  return null;
}

const chromeHiddenRoutes = new Set(["/auth/callback"]);

export default function App() {
  const hideChrome = chromeHiddenRoutes.has(usePathname());

  // Transforms jump rather than move for a reduced-motion visitor, decided as each animation
  // starts. The components only learn the preference a render after hydration.
  return (
    <MotionConfig reducedMotion="user">
      <ShaderBackground />
      <ScrollAndTitle />
      {!hideChrome && <Navbar />}
      <Suspense fallback={<main className="routePending" aria-busy="true" aria-label="Loading page" />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/why-gryt" element={<WhyGryt />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/developers" element={<DevelopersPage />} />
          <Route path="/developers/bots" element={<BotsPage />} />
          <Route path="/developers/addons" element={<AddonsPage />} />
          <Route path="/developers/plugins" element={<PluginsPage />} />
          <Route path="/developers/apis" element={<ApisPage />} />
          <Route path="/developers/voice" element={<VoicePage />} />
          <Route path="/developers/design-system" element={<DesignSystemPage />} />
          <Route path="/developers/contributing" element={<ContributingPage />} />
          <Route path="/built" element={<BuiltPage />} />
          <Route path="/self-hosting" element={<SelfHostingPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/support/invite-a-friend" element={<InviteFriendPage />} />
          <Route path="/support/nobody-can-hear-anyone" element={<NoVoicePage />} />
          <Route path="/support/microphone" element={<MicrophonePage />} />
          <Route path="/support/cant-connect" element={<CantConnectPage />} />
          <Route path="/support/install" element={<InstallPage />} />
          <Route path="/support/voice-wont-start" element={<VoiceWontStartPage />} />
          <Route path="/support/sound-problems" element={<SoundProblemsPage />} />
          <Route path="/support/screen-share-echo" element={<ScreenShareEchoPage />} />
          <Route path="/support/server-clock" element={<ServerClockPage />} />
          <Route path="/support/server-identity" element={<ServerIdentityPage />} />
          <Route path="/support/after-server-restart" element={<ServerRestartPage />} />
          <Route path="/support/my-server-wont-start" element={<ServerWontStartPage />} />
          <Route path="/support/app-updates" element={<AppUpdatesPage />} />
          <Route path="/support/host-a-server" element={<HostServerPage />} />
          <Route path="/support/join-a-server" element={<JoinServerPage />} />
          <Route path="/support/make-an-invite" element={<MakeInvitePage />} />
          <Route path="/support/account-or-guest" element={<AccountOrGuestPage />} />
          <Route path="/support/voice-basics" element={<VoiceBasicsPage />} />
          <Route path="/support/roles" element={<RolesPage />} />
          <Route path="/support/threads" element={<ThreadsPage />} />
          <Route path="/support/custom-emojis" element={<EmojisPage />} />
          <Route path="/support/back-up-a-server" element={<BackupPage />} />
          <Route path="/sponsors" element={<SponsorsPage />} />
          <Route path="/changelog" element={<ChangelogIndex />} />
          <Route path="/changelog/:version" element={<ChangelogEntry />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/community-guidelines" element={<CommunityGuidelines />} />
          <Route path="/guidelines" element={<CommunityGuidelines />} />
          <Route path="/security" element={<SecurityPolicy />} />
          <Route path="/security-policy" element={<SecurityPolicy />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          {/* Without this, an unmatched URL rendered an empty <Routes>: the footer
              sat directly under the navbar with no content and the page kept the
              template title, so it read as a page that had loaded. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!hideChrome && <Footer />}
    </MotionConfig>
  );
}
